import { Component, DestroyRef, ElementRef, NgZone, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';

interface MonacoModel {
  dispose(): void;
  getValue(): string;
  setValue(value: string): void;
  getLineContent(line: number): string;
}

interface MonacoPosition {
  lineNumber: number;
  column: number;
}

interface MonacoEditor {
  dispose(): void;
  layout(): void;
  setModel(model: MonacoModel): void;
  updateOptions(options: Record<string, unknown>): void;
  getTargetAtClientPoint(x: number, y: number): { position: MonacoPosition | null } | null;
  getPosition(): MonacoPosition | null;
  onDidChangeModelContent(callback: () => void): { dispose(): void };
}

interface MonacoApi {
  editor: {
    create(element: HTMLElement, options: Record<string, unknown>): MonacoEditor;
    createModel(value: string, language: string, uri: unknown): MonacoModel;
  };
  Uri: { file(path: string): unknown };
}

const cdnBase = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.55.1/min/';
let monacoPromise: Promise<MonacoApi> | null = null;

function localBase(): string {
  return new URL('assets/monaco/', document.baseURI).href;
}

function loadMonaco(): Promise<MonacoApi> {
  monacoPromise ??= resolveBase().then(startMonaco);
  return monacoPromise;
}

async function resolveBase(): Promise<string> {
  const local = localBase();
  try {
    const response = await fetch(new URL('vs/loader.js', local).href);
    const text = await response.text();
    if (response.ok && text.includes('requirejs')) return local;
  } catch { /* local assets are unavailable */ }
  return cdnBase;
}

function startMonaco(base: string): Promise<MonacoApi> {
  const root = base.endsWith('/') ? base : `${base}/`;
  configureMonacoWorkers(root);
  const host = window as Window & { require?: MonacoRequire; monaco?: MonacoApi };
  return new Promise((resolve, reject) => {
    const start = () => {
      if (!host.require) {
        reject(new Error('Monaco failed to load'));
        return;
      }
      host.require.config({ paths: { vs: new URL('vs', root).href.replace(/\/$/, '') } });
      host.require(['vs/editor/editor.main'], () => {
        if (host.monaco) resolve(host.monaco);
        else reject(new Error('Monaco failed to load'));
      });
    };
    if (host.require) {
      start();
      return;
    }
    const script = document.createElement('script');
    script.src = new URL('vs/loader.js', root).href;
    script.onload = start;
    script.onerror = () => reject(new Error('Monaco failed to load'));
    document.body.appendChild(script);
  });
}

interface MonacoRequire {
  config(options: unknown): void;
  (deps: string[], callback: () => void): void;
}

function configureMonacoWorkers(root: string): void {
  const worker = new URL('vs/base/worker/workerMain.js', root).href;
  const proxy = URL.createObjectURL(new Blob([
    `self.MonacoEnvironment={baseUrl:${JSON.stringify(root)}};importScripts(${JSON.stringify(worker)});`,
  ], { type: 'text/javascript' }));
  (window as Window & { MonacoEnvironment?: { getWorkerUrl(): string } }).MonacoEnvironment = {
    getWorkerUrl: () => proxy,
  };
}

interface JsonKey {
  path: string[];
  line: number;
  startColumn: number;
  endColumn: number;
}

function collectKeys(source: string): JsonKey[] {
  const keys: JsonKey[] = [];
  const parents: string[] = [];
  const keyedFrame: boolean[] = [];
  let pending: string | null = null;
  let i = 0;
  let line = 1;
  let column = 1;
  const consume = () => {
    const ch = source[i];
    i += 1;
    if (ch === '\n') { line += 1; column = 1; }
    else column += 1;
    return ch;
  };
  while (i < source.length) {
    const ch = source[i];
    if (ch === '"') {
      const startLine = line;
      const startColumn = column;
      let raw = '';
      consume();
      while (i < source.length) {
        const current = source[i];
        if (current === '\\') {
          raw += consume();
          if (i < source.length) raw += consume();
          continue;
        }
        if (current === '"') { consume(); break; }
        raw += consume();
      }
      let text = '';
      try { text = JSON.parse(`"${raw}"`) as string; }
      catch { continue; }
      let lookahead = i;
      while (lookahead < source.length && /[ \t\r\n]/.test(source[lookahead])) lookahead += 1;
      if (source[lookahead] === ':') {
        keys.push({ path: [...parents, text], line: startLine, startColumn, endColumn: column - 1 });
        pending = text;
      } else pending = null;
      continue;
    }
    if (ch === '{' || ch === '[') {
      keyedFrame.push(pending !== null);
      if (pending !== null) parents.push(pending);
      pending = null;
      consume();
      continue;
    }
    if (ch === '}' || ch === ']') {
      if (keyedFrame.pop()) parents.pop();
      pending = null;
      consume();
      continue;
    }
    if (pending !== null && !/[\s:]/.test(ch)) pending = null;
    consume();
  }
  return keys;
}

function keyPathAt(source: string, lineNumber: number, column: number): string[] | null {
  const onLine = collectKeys(source).filter(key => key.line === lineNumber);
  if (!onLine.length) return null;
  const inside = onLine.find(key => column >= key.startColumn && column <= key.endColumn);
  if (inside) return inside.path;
  const before = [...onLine].reverse().find(key => key.startColumn <= column);
  return (before ?? onLine[0]).path;
}

function valueAt(root: unknown, path: string[]): unknown {
  let current = root;
  for (const key of path) {
    if (!current || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

function setAt(root: unknown, path: string[], value: string): boolean {
  if (!path.length || !root || typeof root !== 'object') return false;
  let current: unknown = root;
  for (let index = 0; index < path.length - 1; index++) {
    if (!current || typeof current !== 'object') return false;
    current = (current as Record<string, unknown>)[path[index]];
  }
  if (!current || typeof current !== 'object') return false;
  (current as Record<string, unknown>)[path[path.length - 1]] = value;
  return true;
}

@Component({
  selector: 'app-prompt-editor',
  imports: [MatIconModule, TranslocoPipe],
  templateUrl: './prompt-editor.html',
  styleUrl: './prompt-editor.scss',
})
export class PromptEditor {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly surface = viewChild<ElementRef<HTMLElement>>('surface');
  readonly name = input.required<string>();
  readonly value = input.required<string>();
  readonly syncKey = input(0);
  readonly readOnly = input(false);
  readonly valueChange = output<string>();
  readonly detailOpen = output<boolean>();

  readonly editingField = signal(false);
  readonly mounted = signal(false);
  private monaco: MonacoApi | null = null;
  private editor: MonacoEditor | null = null;
  private mainModel: MonacoModel | null = null;
  private detailModel: MonacoModel | null = null;
  private detailPath: string[] = [];
  private current = '';
  private seenSync = -1;
  private applying = false;
  private disposed = false;

  constructor() {
    effect(() => {
      const next = this.value();
      const sync = this.syncKey();
      if (!this.editor || !this.mainModel) return;
      if (sync === this.seenSync && next === this.current) return;
      this.seenSync = sync;
      this.applyExternal(next);
    });
    this.destroyRef.onDestroy(() => {
      this.disposed = true;
      this.editor?.dispose();
      this.mainModel?.dispose();
      this.detailModel?.dispose();
    });
  }

  ngAfterViewInit(): void {
    void loadMonaco().then(monaco => {
      if (this.disposed) return;
      const surface = this.surface()?.nativeElement;
      if (!surface) return;
      const id = `${this.name()}-${crypto.randomUUID()}`;
      this.monaco = monaco;
      this.mainModel = monaco.editor.createModel(this.value(), 'json', monaco.Uri.file(`prompt-${id}.json`));
      this.current = this.value();
      this.seenSync = this.syncKey();
      this.zone.runOutsideAngular(() => {
        const editor = monaco.editor.create(surface, {
          model: this.mainModel,
          automaticLayout: true,
          contextmenu: true,
          readOnly: this.readOnly(),
          wordWrap: 'off',
          stopRenderingLineAfter: -1,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          fontSize: 13,
          fontFamily: 'ui-monospace, Consolas, monospace',
        });
        this.editor = editor;
        editor.onDidChangeModelContent(() => {
          const main = this.mainModel;
          if (this.readOnly() || this.applying || this.editingField() || !main) return;
          const next = main.getValue();
          this.current = next;
          this.zone.run(() => this.valueChange.emit(next));
        });
        requestAnimationFrame(() => editor.layout());
      });
      this.zone.run(() => this.mounted.set(true));
    }).catch(() => undefined);
  }

  onFallback(event: Event): void {
    if (this.readOnly()) return;
    const next = (event.target as HTMLTextAreaElement).value;
    this.current = next;
    this.valueChange.emit(next);
  }

  editDetail(event: MouseEvent): void {
    const editor = this.editor;
    const main = this.mainModel;
    if (!editor || !main || !this.monaco || this.editingField()) return;
    const position = editor.getTargetAtClientPoint(event.clientX, event.clientY)?.position ?? editor.getPosition();
    if (!position) return;
    const source = main.getValue();
    const path = keyPathAt(source, position.lineNumber, position.column);
    if (!path) return;
    let parsed: unknown;
    try { parsed = JSON.parse(source); }
    catch { return; }
    const field = valueAt(parsed, path);
    if (typeof field !== 'string') return;
    this.detailPath = path;
    if (!this.detailModel) {
      this.detailModel = this.monaco.editor.createModel(field, 'plaintext', this.monaco.Uri.file(`prompt-field-${this.name()}-${crypto.randomUUID()}.txt`));
    } else {
      this.detailModel.setValue(field);
    }
    this.editingField.set(true);
    this.swap(this.detailModel, 'on');
    this.detailOpen.emit(true);
  }

  closeDetail(): void {
    if (!this.editingField() || !this.mainModel) return;
    this.editingField.set(false);
    this.swap(this.mainModel, 'off');
    this.detailOpen.emit(false);
  }

  applyDetail(): void {
    const main = this.mainModel;
    const detail = this.detailModel;
    if (this.readOnly() || !main || !detail || !this.detailPath.length) return;
    let parsed: unknown;
    try { parsed = JSON.parse(main.getValue()); }
    catch { return; }
    if (!setAt(parsed, this.detailPath, detail.getValue())) return;
    const next = JSON.stringify(parsed, null, 2);
    this.current = next;
    this.applying = true;
    main.setValue(next);
    this.applying = false;
    this.editingField.set(false);
    this.swap(main, 'off');
    this.detailOpen.emit(false);
    this.valueChange.emit(next);
  }

  private applyExternal(next: string): void {
    if (!this.mainModel || !this.editor) return;
    this.current = next;
    if (this.editingField()) {
      this.editingField.set(false);
      this.detailOpen.emit(false);
    }
    this.applying = true;
    this.mainModel.setValue(next);
    this.editor.setModel(this.mainModel);
    this.editor.updateOptions({ wordWrap: 'off', readOnly: this.readOnly() });
    this.applying = false;
  }

  private swap(model: MonacoModel, wrap: 'on' | 'off'): void {
    if (!this.editor) return;
    this.applying = true;
    this.editor.setModel(model);
    this.editor.updateOptions({ wordWrap: wrap, readOnly: this.readOnly() });
    this.applying = false;
    this.editor.layout();
  }
}
